import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { WebsiteType } from 'modules/Const'
import useWebsiteBuilder, {
    WebsiteStatus,
    WebsiteBuilderContent,
    WebsiteBuilderContentType,
    PresavePlatform,
    PresaveWebsiteBuilderStyle,
    TourStyle,
    StreamingPlatformsStyle,
    CollectDataInputType,
    WebsiteTextAlign
} from 'Hooks/useWebsiteBuilder'
import useCurrentBrand from 'Hooks/useCurrentBrand'
import { STREAMING_PLATFORMS_WEBSITE_CONTENT } from 'Hooks/useWebsiteBuilder'
import { WebsiteBuilderDataPayload } from 'api/websiteBuilderApi'
import axios from 'axios'

const IS_MOCK = false
const MAESTRO_MESSAGES_MOCK_DATA = [
    {
        id: 1,
        text: 'Hello! I\'m Maestro, your AI assistant. How can I help you today?',
        createdAt: '2024-03-20T10:30:00Z',
        isUser: false
    },
    {
        id: 2,
        text: 'Hi! I need help setting up my ad campaign for Instagram.',
        createdAt: '2024-03-20T10:30:45Z',
        isUser: true
    },
    {
        id: 3,
        text: 'I\'d be happy to help you set up your Instagram ad campaign. What\'s your primary marketing objective?',
        createdAt: '2024-03-20T10:31:15Z',
        isUser: false
    },
    {
        id: 4,
        text: 'I want to increase brand awareness and reach more potential customers.',
        createdAt: '2024-03-20T10:32:00Z',
        isUser: true
    },
    {
        id: 5,
        text: 'Great choice! For brand awareness, I recommend starting with a reach campaign. Would you like me to guide you through the setup process?',
        createdAt: '2024-03-20T10:32:30Z',
        isUser: false
    },
    {
        id: 6,
        text: 'Yes, please! What should I do first?',
        createdAt: '2024-03-20T10:33:15Z',
        isUser: true
    },
    {
        id: 7,
        text: 'First, let\'s connect your Instagram business account if you haven\'t already. Have you done this step?',
        createdAt: '2024-03-20T10:33:45Z',
        isUser: false
    },
    {
        id: 8,
        text: 'Not yet, how do I do that?',
        createdAt: '2024-03-20T10:34:30Z',
        isUser: true
    },
    {
        id: 9,
        text: 'No problem! Go to the Connections tab in your dashboard and click on "Connect Instagram". You\'ll need to log in to your Facebook account that manages your Instagram business profile.',
        createdAt: '2024-03-20T10:35:00Z',
        isUser: false
    },
    {
        id: 10,
        text: 'Got it, thanks! I\'ll try that now.',
        createdAt: '2024-03-20T10:35:45Z',
        isUser: true
    }
]

const TAGS_INIT = [
    'How can I grow my followers?',
    'How can I get more likes?',
    'How can I get more comments?',
    'How can I get more views?',
    'How many new fans do i have?'
]

/** Agent ID for ElevenLabs voice AI */
const ELEVENLABS_AGENT_ID = 'AS15w0BLcmvbvhn897qi' // Replace with your actual agent ID

export interface MessageItem {
    id: number
    text: string
    createdAt: string
    isUser: boolean
}

/** Client tool function parameters for website builder operations */
export interface CreateWebsiteParams {
    /** Type of website to create */
    websiteType: WebsiteType
    /** Optional website name */
    name?: string
}

export interface UpdateWebsiteColorParams {
    /** Website ID to update */
    websiteId: number
    /** Background color in hex format (#RRGGBB) */
    backgroundColor: string
    /** Secondary color in hex format (#RRGGBB) */
    secondaryColor: string
}

export interface UpdateWebsiteFontParams {
    /** Website ID to update */
    websiteId: number
    /** Font family name */
    fontFamily: string
}

export interface PublishWebsiteParams {
    /** Website ID to publish */
    websiteId: number
}

// Interface for website builder style
interface WebsiteBuilderStyle {
    backgroundColor: string;
    secondaryColor: string;
    textColor: string;
    fontFamily: string;
    textAlign: WebsiteTextAlign;
    isSymphonyLogoEnabled: boolean;
    symphonyLogoStyle: string; // Using string for SymphonyLogoStyleKey
}

interface MaestroChatState {
    // State
    isChatOpen: boolean
    isLoadingMessages: boolean
    messages: MessageItem[]
    messageText: string
    maestroTags: string[]
    isStreaming: boolean
    currentStreamedText: string

    // Voice AI State
    isVoiceEnabled: boolean
    isVoiceListening: boolean
    isVoiceSpeaking: boolean
    voiceVolume: number
    conversationId: string | null
    isVoiceMode: boolean

    // Website Builder Client Tool State
    isWebsiteBuilderAction: boolean
    lastWebsiteBuilderAction: string | null
    lastWebsiteBuilderResult: any | null

    // Actions
    setIsChatOpen: (open: boolean) => void
    setIsLoadingMessages: (loading: boolean) => void
    setMessages: (messages: MessageItem[]) => void
    setMessageText: (text: string) => void
    setMaestroTags: (tags: string[]) => void
    setIsStreaming: (streaming: boolean) => void
    setCurrentStreamedText: (text: string) => void
    appendToStreamedText: (text: string) => void

    // Voice AI Actions
    setIsVoiceEnabled: (enabled: boolean) => void
    setIsVoiceListening: (listening: boolean) => void
    setIsVoiceSpeaking: (speaking: boolean) => void
    setVoiceVolume: (volume: number) => void
    setConversationId: (id: string | null) => void
    setIsVoiceMode: (isVoiceMode: boolean) => void

    // Website Builder Client Tool Actions
    setIsWebsiteBuilderAction: (isAction: boolean) => void
    setLastWebsiteBuilderAction: (action: string | null) => void
    setLastWebsiteBuilderResult: (result: any | null) => void

    // Methods
    sendMessage: (text: string) => void
    getMessages: () => void
    clearChatHistory: () => void
    completeStream: () => void

    // Voice AI Methods
    startVoiceSession: () => Promise<void>
    endVoiceSession: () => Promise<void>
    toggleVoice: () => Promise<void>
    adjustVoiceVolume: (volume: number) => Promise<void>

    // Website Builder Client Tool Methods
    createWebsite: (params: CreateWebsiteParams) => Promise<any>
    updateWebsiteColor: (params: UpdateWebsiteColorParams) => Promise<any>
    updateWebsiteFont: (params: UpdateWebsiteFontParams) => Promise<any>
    publishWebsite: (params: PublishWebsiteParams) => Promise<any>
}

const useMaestroChat = create(
    immer<MaestroChatState>((set, get) => ({
        // State
        isChatOpen: false,
        isLoadingMessages: false,
        messages: [],
        messageText: '',
        maestroTags: TAGS_INIT,
        isStreaming: false,
        currentStreamedText: '',

        // Voice AI State
        isVoiceEnabled: false,
        isVoiceListening: false,
        isVoiceSpeaking: false,
        voiceVolume: 0.8,
        conversationId: null,
        isVoiceMode: false,

        // Website Builder Client Tool State
        isWebsiteBuilderAction: false,
        lastWebsiteBuilderAction: null,
        lastWebsiteBuilderResult: null,

        // Actions
        setIsChatOpen: (open) => { set((state) => { state.isChatOpen = open }) },
        setIsLoadingMessages: (loading) => { set((state) => { state.isLoadingMessages = loading }) },
        setMessages: (messages) => { set((state) => { state.messages = messages }) },
        setMessageText: (text) => { set((state) => { state.messageText = text }) },
        setMaestroTags: (tags) => { set((state) => { state.maestroTags = tags }) },
        setIsStreaming: (streaming) => { set((state) => { state.isStreaming = streaming }) },
        setCurrentStreamedText: (text) => { set((state) => { state.currentStreamedText = text }) },
        appendToStreamedText: (text) => { set((state) => { state.currentStreamedText += text }) },

        // Voice AI Actions
        setIsVoiceEnabled: (enabled) => { set((state) => { state.isVoiceEnabled = enabled }) },
        setIsVoiceListening: (listening) => { set((state) => { state.isVoiceListening = listening }) },
        setIsVoiceSpeaking: (speaking) => { set((state) => { state.isVoiceSpeaking = speaking }) },
        setVoiceVolume: (volume) => { set((state) => { state.voiceVolume = volume }) },
        setConversationId: (id) => { set((state) => { state.conversationId = id }) },
        setIsVoiceMode: (isVoiceMode) => { set((state) => { state.isVoiceMode = isVoiceMode }) },

        // Website Builder Client Tool Actions
        setIsWebsiteBuilderAction: (isAction) => { set((state) => { state.isWebsiteBuilderAction = isAction }) },
        setLastWebsiteBuilderAction: (action) => { set((state) => { state.lastWebsiteBuilderAction = action }) },
        setLastWebsiteBuilderResult: (result) => { set((state) => { state.lastWebsiteBuilderResult = result }) },

        // Complete the streaming and add the message to the chat history
        completeStream: () => {
            const { currentStreamedText, messages } = get();
            
            if (currentStreamedText.trim()) {
                set((state) => {
                    state.messages.push({
                        id: state.messages.length + 1,
                        text: currentStreamedText,
                        createdAt: new Date().toISOString(),
                        isUser: false
                    });
                    state.currentStreamedText = '';
                    state.isStreaming = false;
                });
            }
        },

        // Methods
        sendMessage: async (text: string) => {
            if (!text.trim()) return;

            const { currentBrand } = useCurrentBrand.getState();
            if (!currentBrand?.id) {
                console.error('No brand selected');
                return;
            }

            set((state) => {
                state.isLoadingMessages = true;
                state.messages.push({
                    id: state.messages.length + 1,
                    text,
                    createdAt: new Date().toISOString(),
                    isUser: true
                });
            });

            try {
                // Get current message history to send to API
                const currentMessages = [...get().messages];

                if (IS_MOCK) {
                    // Mock streaming response for testing
                    set((state) => { 
                        state.isStreaming = true;
                        state.currentStreamedText = '';
                    });

                    const mockResponse = `I received your message: "${text}". How can I help you further?`;
                    const mockChunks = mockResponse.split(' ');
                    
                    for (let i = 0; i < mockChunks.length; i++) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                        set((state) => {
                            state.currentStreamedText += mockChunks[i] + ' ';
                        });
                    }
                    
                    // Finish the stream
                    setTimeout(() => {
                        get().completeStream();
                        set((state) => { state.isLoadingMessages = false });
                    }, 500);
                } else {
                    // Real API streaming call
                    try {
                        // Set up streaming state
                        set((state) => { 
                            state.isStreaming = true;
                            state.currentStreamedText = '';
                        });

                        // Make streaming API request with proper streaming config
                        const response = await axios({
                            method: 'POST',
                            url: `${process.env.REACT_APP_API_URL}/agents/maestro-streaming-chat`,
                            data: { 
                                messages: currentMessages,
                                brandId: currentBrand.id
                            },
                            responseType: 'text',
                            onDownloadProgress: (progressEvent) => {
                                const dataChunk = progressEvent.currentTarget.response;
                                
                                // Extract only the new text since the last update
                                const newText = dataChunk.substring(get().currentStreamedText.length);
                                
                                if (newText) {
                                    set((state) => { 
                                        state.currentStreamedText += newText;
                                    });
                                }
                            }
                        });

                        // Finish the stream
                        get().completeStream();
                        set((state) => { state.isLoadingMessages = false });
                    } catch (error) {
                        console.error('Error with streaming API:', error);
                        
                        // Fall back to regular API if streaming fails
                        try {
                            const fallbackResponse = await axios.post(`${process.env.REACT_APP_API_URL}/agents/maestro-chat`, { 
                                messages: currentMessages,
                                brandId: currentBrand.id
                            });

                            if (fallbackResponse.data && fallbackResponse.data.messages && Array.isArray(fallbackResponse.data.messages)) {
                                set((state) => {
                                    state.messages = fallbackResponse.data.messages;
                                    state.isLoadingMessages = false;
                                    state.isStreaming = false;
                                });
                            } else {
                                throw new Error('Invalid response format');
                            }
                        } catch (fallbackError) {
                            console.error('Fallback API error:', fallbackError);
                            set((state) => { 
                                state.isLoadingMessages = false;
                                state.isStreaming = false;
                                // Add error message
                                state.messages.push({
                                    id: state.messages.length + 1,
                                    text: 'Sorry, I encountered an error processing your request. Please try again later.',
                                    createdAt: new Date().toISOString(),
                                    isUser: false
                                });
                            });
                        }
                    }
                }
            } catch (error) {
                console.error('Error sending message:', error);
                set((state) => { 
                    state.isLoadingMessages = false;
                    state.isStreaming = false;
                });
            }
        },
        
        getMessages: async () => {
            set((state) => { state.isLoadingMessages = true });
            try {
                if (IS_MOCK) {
                    set((state) => { state.messages = MAESTRO_MESSAGES_MOCK_DATA });
                } else {
                    const { currentBrand } = useCurrentBrand.getState();
                    
                    if (!currentBrand?.id) {
                        console.error('No brand selected');
                        set((state) => { 
                            state.messages = [{
                                id: 1,
                                text: 'Hello! I\'m Maestro, your AI assistant. How can I help you today?',
                                createdAt: new Date().toISOString(),
                                isUser: false
                            }];
                        });
                        return;
                    }

                    try {
                        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/agents/maestro-chat`, { 
                            messages: [{
                                id: 1,
                                text: 'Hello',
                                createdAt: new Date().toISOString(),
                                isUser: true
                            }],
                            brandId: currentBrand.id
                        });

                        if (response.data && response.data.messages && Array.isArray(response.data.messages)) {
                            set((state) => { state.messages = response.data.messages });
                        } else {
                            // Default welcome message if API doesn't return proper format
                            set((state) => { 
                                state.messages = [{
                                    id: 1,
                                    text: 'Hello! I\'m Maestro, your AI assistant. How can I help you today?',
                                    createdAt: new Date().toISOString(),
                                    isUser: false
                                }];
                            });
                        }
                    } catch (error) {
                        console.error('Error fetching messages from API:', error);
                        // Default welcome message on error
                        set((state) => { 
                            state.messages = [{
                                id: 1,
                                text: 'Hello! I\'m Maestro, your AI assistant. How can I help you today?',
                                createdAt: new Date().toISOString(),
                                isUser: false
                            }];
                        });
                    }
                }
            } catch (error) {
                console.error('Error getting messages:', error);
                // Default welcome message on error
                set((state) => { 
                    state.messages = [{
                        id: 1,
                        text: 'Hello! I\'m Maestro, your AI assistant. How can I help you today?',
                        createdAt: new Date().toISOString(),
                        isUser: false
                    }];
                });
            } finally {
                set((state) => { state.isLoadingMessages = false });
            }
        },

        clearChatHistory: () => {
            set((state) => {
                state.messages = [{
                    id: 1,
                    text: 'Hello! I\'m Maestro, your AI assistant. How can I help you today?',
                    createdAt: new Date().toISOString(),
                    isUser: false
                }]
            })
        },

        // Voice AI Methods
        startVoiceSession: async () => {
            try {
                // This function will be implemented in the component using the useConversation hook
                set((state) => {
                    state.isVoiceEnabled = true
                    state.isVoiceListening = true
                    state.isVoiceMode = true
                })
            } catch (error) {
                console.error('Error starting voice session:', error)
                set((state) => {
                    state.isVoiceEnabled = false
                    state.isVoiceListening = false
                    state.isVoiceMode = false
                })
            }
        },

        endVoiceSession: async () => {
            try {
                // This function will be implemented in the component using the useConversation hook
                set((state) => {
                    state.isVoiceEnabled = false
                    state.isVoiceListening = false
                    state.isVoiceSpeaking = false
                    state.conversationId = null
                    state.isVoiceMode = false
                })
            } catch (error) {
                console.error('Error ending voice session:', error)
            }
        },

        toggleVoice: async () => {
            const isEnabled = get().isVoiceEnabled
            if (isEnabled) {
                await get().endVoiceSession()
            } else {
                await get().startVoiceSession()
            }
        },

        adjustVoiceVolume: async (volume: number) => {
            set((state) => { state.voiceVolume = volume })
            // This function will be implemented in the component using the useConversation hook
        },

        // Website Builder Client Tool Methods
        createWebsite: async (params: CreateWebsiteParams) => {
            set((state) => {
                state.isWebsiteBuilderAction = true
                state.lastWebsiteBuilderAction = 'createWebsite'
            })

            try {
                // Get the current brand
                const { currentBrand } = useCurrentBrand.getState()
                const brandId = currentBrand?.id

                if (!brandId) {
                    throw new Error('No brand selected')
                }

                // Initialize website builder store with defaults
                const websiteBuilder = useWebsiteBuilder.getState()

                // Generate a slug based on the website name or use default
                const slug = params.name
                    ? params.name.toLowerCase().replace(/[^a-z0-9]/g, '-')
                    : `new-website-${Date.now()}`

                // Set basic website properties
                websiteBuilder.setWebsiteSlugText(slug)
                websiteBuilder.resetWebsiteBuilder() // Reset to default styles and empty content
                websiteBuilder.setInitialWebsiteType(params.websiteType)

                // Create default content based on website type
                let initialContent: WebsiteBuilderContent[] = []

                if (params.websiteType === WebsiteType.PRESAVE) {
                    // Create a presave website with basic content
                    initialContent = [
                        {
                            id: `text-${Date.now()}-1`,
                            type: WebsiteBuilderContentType.TEXT,
                            contentIndex: 0,
                            isContentBackgroundColorEnabled: false,
                            isContentSecondaryColorEnabled: false,
                            textContent: {
                                title: params.name || 'New Presave',
                                subtitle: 'Pre-save my upcoming release',
                                isSubtitle: true
                            }
                        },
                        {
                            id: `presave-${Date.now()}-2`,
                            type: WebsiteBuilderContentType.PRESAVE,
                            contentIndex: 1,
                            isContentBackgroundColorEnabled: false,
                            isContentSecondaryColorEnabled: false,
                            presaveContent: {
                                title: 'Pre-save',
                                style: PresaveWebsiteBuilderStyle.TRADITIONAL,
                                hideReleaseDate: false,
                                presave: {
                                    boosts: {
                                        auto_presaves: true,
                                        boost_follows: false,
                                        collect_emails: true
                                    },
                                    platforms: [
                                        { name: PresavePlatform.SPOTIFY, isEnabled: true },
                                        { name: PresavePlatform.APPLE_MUSIC, isEnabled: true },
                                        { name: PresavePlatform.AUDIOMACK, isEnabled: false },
                                        { name: PresavePlatform.SOUNDCLOUD, isEnabled: false },
                                        { name: PresavePlatform.DEEZER, isEnabled: false }
                                    ],
                                    release_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
                                }
                            }
                        }
                    ]
                } else if (params.websiteType === WebsiteType.RECORD_STREAMS) {
                    // Create a record streams website with basic content
                    initialContent = [
                        {
                            id: `text-${Date.now()}-1`,
                            type: WebsiteBuilderContentType.TEXT,
                            contentIndex: 0,
                            isContentBackgroundColorEnabled: false,
                            isContentSecondaryColorEnabled: false,
                            textContent: {
                                title: params.name || 'My Music',
                                subtitle: 'Listen to my music on your favorite platform',
                                isSubtitle: true
                            }
                        },
                        {
                            id: `streaming-${Date.now()}-2`,
                            type: WebsiteBuilderContentType.STREAMING_PLATFORMS,
                            contentIndex: 1,
                            isContentBackgroundColorEnabled: false,
                            isContentSecondaryColorEnabled: false,
                            streamingPlatformsContent: {
                                title: 'Listen Now',
                                // Convert to the required format for PlatformItem
                                platforms: STREAMING_PLATFORMS_WEBSITE_CONTENT.map(platform => ({
                                    title: platform.name,
                                    description: `Listen on ${platform.name}`,
                                    name: platform.name,
                                    icon: platform.name.toLowerCase(),
                                    link: platform.link,
                                    isEnabled: ['spotify', 'apple_music'].includes(platform.name),
                                    placeholder: platform.placeholder
                                })),
                                style: StreamingPlatformsStyle.BOXES
                            }
                        }
                    ]
                } else if (params.websiteType === WebsiteType.DATA_COLLECTOR) {
                    // Create a data collector website with basic content
                    initialContent = [
                        {
                            id: `text-${Date.now()}-1`,
                            type: WebsiteBuilderContentType.TEXT,
                            contentIndex: 0,
                            isContentBackgroundColorEnabled: false,
                            isContentSecondaryColorEnabled: false,
                            textContent: {
                                title: params.name || 'Join My List',
                                subtitle: 'Subscribe to get updates about my music',
                                isSubtitle: true
                            }
                        },
                        {
                            id: `collect-data-${Date.now()}-2`,
                            type: WebsiteBuilderContentType.COLLECT_DATA,
                            contentIndex: 1,
                            isContentBackgroundColorEnabled: false,
                            isContentSecondaryColorEnabled: false,
                            collectDataContent: {
                                title: 'Subscribe',
                                buttonText: 'Subscribe',
                                postSubscribeMessage: 'Thank you for subscribing!',
                                isBothEmailPhone: false,
                                isShowTitles: true,
                                info: [
                                    {
                                        title: 'Name',
                                        placeholder: 'Your name',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: false
                                    },
                                    {
                                        title: 'Email',
                                        placeholder: 'Your email',
                                        type: CollectDataInputType.TEXT,
                                        isRequired: true
                                    }
                                ]
                            }
                        }
                    ]
                } else if (params.websiteType === WebsiteType.TOUR) {
                    // Create a tour website with basic content
                    initialContent = [
                        {
                            id: `text-${Date.now()}-1`,
                            type: WebsiteBuilderContentType.TEXT,
                            contentIndex: 0,
                            isContentBackgroundColorEnabled: false,
                            isContentSecondaryColorEnabled: false,
                            textContent: {
                                title: params.name || 'My Tour Dates',
                                subtitle: 'Come see me live on tour',
                                isSubtitle: true
                            }
                        },
                        {
                            id: `tour-${Date.now()}-2`,
                            type: WebsiteBuilderContentType.TOUR,
                            contentIndex: 1,
                            isContentBackgroundColorEnabled: false,
                            isContentSecondaryColorEnabled: false,
                            tourContent: {
                                title: 'Tour Dates',
                                tourDates: [],
                                hidePastTourDates: true,
                                style: TourStyle.STACKED
                            }
                        }
                    ]
                }

                // Set the content in the website builder store
                websiteBuilder.setWebsiteContent(initialContent)

                // Create the website
                const result = await websiteBuilder.createWebsite()

                if (!result) {
                    throw new Error('Failed to create website')
                }

                // Prepare response
                const response = {
                    success: true,
                    websiteId: result.id,
                    websiteType: params.websiteType,
                    name: params.name || 'New Website',
                    status: result.status,
                    createdAt: result.createdAt,
                    slug: result.slug,
                    websiteUrl: `https://${currentBrand?.slug}.symphony.is/${result.slug}`
                }

                set((state) => { state.lastWebsiteBuilderResult = response })
                return response
            } catch (error) {
                console.error('Error creating website:', error)
                const errorResult = { success: false, error: 'Failed to create website' }
                set((state) => { state.lastWebsiteBuilderResult = errorResult })
                return errorResult
            } finally {
                set((state) => { state.isWebsiteBuilderAction = false })
            }
        },

        updateWebsiteColor: async (params: UpdateWebsiteColorParams) => {
            set((state) => {
                state.isWebsiteBuilderAction = true
                state.lastWebsiteBuilderAction = 'updateWebsiteColor'
            })

            try {
                // Get the website builder store
                const websiteBuilder = useWebsiteBuilder.getState()

                // First load the website to update
                await websiteBuilder.getWebsite({ websiteId: params.websiteId })

                // Get the current website style
                const currentStyle = { ...websiteBuilder.websiteStyle }

                // Set the updated style with required properties
                websiteBuilder.setWebsiteStyle({
                    ...currentStyle,
                    backgroundColor: params.backgroundColor,
                    secondaryColor: params.secondaryColor
                })

                // Update the website
                const result = await websiteBuilder.updateWebsite({ websiteId: params.websiteId })

                if (!result) {
                    throw new Error('Failed to update website')
                }

                // Prepare response
                const response = {
                    success: true,
                    websiteId: params.websiteId,
                    backgroundColor: params.backgroundColor,
                    secondaryColor: params.secondaryColor,
                    updatedAt: result.updatedAt
                }

                set((state) => { state.lastWebsiteBuilderResult = response })
                return response
            } catch (error) {
                console.error('Error updating website color:', error)
                const errorResult = { success: false, error: 'Failed to update website color' }
                set((state) => { state.lastWebsiteBuilderResult = errorResult })
                return errorResult
            } finally {
                set((state) => { state.isWebsiteBuilderAction = false })
            }
        },

        updateWebsiteFont: async (params: UpdateWebsiteFontParams) => {
            set((state) => {
                state.isWebsiteBuilderAction = true
                state.lastWebsiteBuilderAction = 'updateWebsiteFont'
            })

            try {
                // Get the website builder store
                const websiteBuilder = useWebsiteBuilder.getState()

                // First load the website to update
                await websiteBuilder.getWebsite({ websiteId: params.websiteId })

                // Get the current website style
                const currentStyle = { ...websiteBuilder.websiteStyle }

                // Set the updated style with required properties
                websiteBuilder.setWebsiteStyle({
                    ...currentStyle,
                    fontFamily: params.fontFamily
                })

                // Update the website
                const result = await websiteBuilder.updateWebsite({ websiteId: params.websiteId })

                if (!result) {
                    throw new Error('Failed to update website')
                }

                // Prepare response
                const response = {
                    success: true,
                    websiteId: params.websiteId,
                    fontFamily: params.fontFamily,
                    updatedAt: result.updatedAt
                }

                set((state) => { state.lastWebsiteBuilderResult = response })
                return response
            } catch (error) {
                console.error('Error updating website font:', error)
                const errorResult = { success: false, error: 'Failed to update website font' }
                set((state) => { state.lastWebsiteBuilderResult = errorResult })
                return errorResult
            } finally {
                set((state) => { state.isWebsiteBuilderAction = false })
            }
        },

        publishWebsite: async (params: PublishWebsiteParams) => {
            set((state) => {
                state.isWebsiteBuilderAction = true
                state.lastWebsiteBuilderAction = 'publishWebsite'
            })

            try {
                // Get the website builder store
                const websiteBuilder = useWebsiteBuilder.getState()

                // Load the website to publish
                await websiteBuilder.getWebsite({ websiteId: params.websiteId })

                // Create the payload with the PUBLISHED status
                const websiteData = websiteBuilder.websiteBuilderData

                if (!websiteData) {
                    throw new Error('No website data found')
                }

                const { currentBrand } = useCurrentBrand.getState()

                // Create the payload
                const publishPayload: WebsiteBuilderDataPayload = {
                    brandId: currentBrand?.id || websiteData.brandId,
                    slug: websiteData.slug,
                    status: WebsiteStatus.PUBLISHED,
                    websiteContent: websiteBuilder.websiteContent,
                    websiteStyle: websiteBuilder.websiteStyle
                }

                // Update the website with the published status
                const result = await websiteBuilder.updateWebsite({
                    websiteId: params.websiteId,
                    websiteBuilderPayload: publishPayload
                })

                if (!result) {
                    throw new Error('Failed to publish website')
                }

                // Prepare response
                const response = {
                    success: true,
                    websiteId: params.websiteId,
                    status: WebsiteStatus.PUBLISHED,
                    publishedAt: result.updatedAt,
                    websiteUrl: `https://${currentBrand?.slug}.symphony.is/${result.slug}`
                }

                set((state) => { state.lastWebsiteBuilderResult = response })
                return response
            } catch (error) {
                console.error('Error publishing website:', error)
                const errorResult = { success: false, error: 'Failed to publish website' }
                set((state) => { state.lastWebsiteBuilderResult = errorResult })
                return errorResult
            } finally {
                set((state) => { state.isWebsiteBuilderAction = false })
            }
        }
    }))
)

export default useMaestroChat
